import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  advanceRmaStatus,
  cancelRma,
  getRma,
  receiveRmaReplacement,
  resolveRma,
  type SupplierRmaItem,
} from '@/lib/api/inventory-rmas.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const NEXT: Record<string, string | undefined> = {
  preparing: 'ready_to_ship',
  ready_to_ship: 'sent',
  sent: 'awaiting_supplier',
};

type ItemDraft = {
  itemStatus: 'credited' | 'denied' | 'replaced';
  creditAmount: string;
  resolutionNote: string;
};

export function RmaDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['inventory-rma', id],
    queryFn: () => getRma(id),
    enabled: Boolean(id),
  });
  const [mode, setMode] = useState<'header' | 'per_item'>('header');
  const [headerCredit, setHeaderCredit] = useState('');
  const [memo, setMemo] = useState('');
  const [drafts, setDrafts] = useState<Record<string, ItemDraft>>({});

  const advanceMutation = useMutation({
    mutationFn: (status: string) => advanceRmaStatus(id, status),
    onSuccess: () => {
      toastSuccess('Status updated');
      void queryClient.invalidateQueries({ queryKey: ['inventory-rma', id] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelRma(id),
    onSuccess: () => {
      toastSuccess('RMA cancelled');
      void queryClient.invalidateQueries({ queryKey: ['inventory-rma', id] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const receiveMutation = useMutation({
    mutationFn: () => {
      const items = (query.data?.items ?? []).map((item) => ({
        rmaItemId: item.id,
        quantityReplaced: item.quantityReturned,
      }));
      return receiveRmaReplacement(id, items);
    },
    onSuccess: () => {
      toastSuccess('Replacement received');
      void queryClient.invalidateQueries({ queryKey: ['inventory-rma', id] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const resolveMutation = useMutation({
    mutationFn: () => {
      if (mode === 'header') {
        return resolveRma(id, {
          mode: 'header',
          creditAmount: Number(headerCredit),
          creditMemoNumber: memo || undefined,
        });
      }
      return resolveRma(id, {
        mode: 'per_item',
        creditMemoNumber: memo || undefined,
        items: (query.data?.items ?? []).map((item) => {
          const draft = drafts[item.id] ?? { itemStatus: 'credited' as const, creditAmount: '', resolutionNote: '' };
          return {
            id: item.id,
            itemStatus: draft.itemStatus,
            creditAmount: draft.itemStatus === 'credited' ? Number(draft.creditAmount || 0) : undefined,
            resolutionNote: draft.resolutionNote || undefined,
          };
        }),
      });
    },
    onSuccess: () => {
      toastSuccess('RMA resolved');
      void queryClient.invalidateQueries({ queryKey: ['inventory-rma', id] });
      void queryClient.invalidateQueries({ queryKey: ['inventory-rmas'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const rma = query.data;
  if (query.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;
  if (!rma) return <div className="p-6 text-red-600">RMA not found</div>;

  const nextStatus = NEXT[rma.status];
  const canResolve = ['sent', 'awaiting_supplier', 'ready_to_ship', 'preparing'].includes(rma.status);

  function draftFor(item: SupplierRmaItem): ItemDraft {
    return drafts[item.id] ?? { itemStatus: 'credited', creditAmount: item.unitCost, resolutionNote: '' };
  }

  return (
    <div className="space-y-5">
      <div>
        <Link to="/inventory/rmas" className="text-sm font-medium text-slate-500 hover:text-slate-800">
          ← Back to RMAs
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-950">{rma.rmaNumber}</h1>
            <p className="text-sm text-slate-500">
              {rma.supplierName} · {prettyLabel(rma.status)} · {formatDateTime(rma.createdAt)}
              {rma.creditAmount ? ` · Credit ${formatCurrency(rma.creditAmount)}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {nextStatus ? (
              <button
                type="button"
                className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                onClick={() => advanceMutation.mutate(nextStatus)}
              >
                Advance to {prettyLabel(nextStatus)}
              </button>
            ) : null}
            {rma.resolutionType === 'replacement' && rma.status !== 'replaced' && rma.status !== 'cancelled' ? (
              <button
                type="button"
                className="rounded-xl border px-3 py-2 text-sm font-semibold"
                onClick={() => receiveMutation.mutate()}
              >
                Receive replacement
              </button>
            ) : null}
            {rma.status === 'preparing' ? (
              <button
                type="button"
                className="rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-700"
                onClick={() => cancelMutation.mutate()}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-5 py-3">Item</th>
              <th className="px-5 py-3">Qty</th>
              <th className="px-5 py-3">Cost</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Credit</th>
              <th className="px-5 py-3">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(rma.items ?? []).map((item) => (
              <tr key={item.id}>
                <td className="px-5 py-3 font-medium">
                  {item.itemName}
                  {item.inventorySerialId ? <div className="text-xs text-slate-500">Serialized device</div> : null}
                </td>
                <td className="px-5 py-3">{item.quantityReturned}</td>
                <td className="px-5 py-3">{formatCurrency(item.totalCost)}</td>
                <td className="px-5 py-3">{prettyLabel(item.itemStatus ?? 'pending')}</td>
                <td className="px-5 py-3">{item.creditAmount ? formatCurrency(item.creditAmount) : '—'}</td>
                <td className="px-5 py-3 text-slate-600">{item.resolutionNote || item.denialReason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canResolve && rma.status !== 'credited' && rma.status !== 'cancelled' ? (
        <form
          className="space-y-4 rounded-2xl border bg-white p-5 shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            resolveMutation.mutate();
          }}
        >
          <h2 className="font-semibold text-slate-950">Resolve credit</h2>
          <p className="text-sm text-slate-600">
            Record the supplier credit. Denied units are written off. The note is what accounting uses to tell a
            bin from a return shipment.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm font-semibold ${mode === 'header' ? 'bg-primary text-white' : 'border'}`}
              onClick={() => setMode('header')}
            >
              One credit for the batch
            </button>
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm font-semibold ${mode === 'per_item' ? 'bg-primary text-white' : 'border'}`}
              onClick={() => setMode('per_item')}
            >
              Credit some, deny others
            </button>
          </div>
          {mode === 'header' ? (
            <label className="block text-sm font-medium">
              Credit amount
              <input
                type="number"
                min="0"
                step="0.01"
                required
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={headerCredit}
                onChange={(event) => setHeaderCredit(event.target.value)}
              />
            </label>
          ) : (
            <div className="space-y-3">
              {(rma.items ?? []).map((item) => {
                const draft = draftFor(item);
                return (
                  <div key={item.id} className="grid gap-2 rounded-xl border p-3 md:grid-cols-4">
                    <div className="font-medium">{item.itemName}</div>
                    <select
                      className="rounded-xl border px-3 py-2"
                      value={draft.itemStatus}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [item.id]: { ...draft, itemStatus: event.target.value as ItemDraft['itemStatus'] },
                        }))
                      }
                    >
                      <option value="credited">Credited</option>
                      <option value="denied">Denied</option>
                      <option value="replaced">Replaced</option>
                    </select>
                    {draft.itemStatus === 'credited' ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="rounded-xl border px-3 py-2"
                        value={draft.creditAmount}
                        onChange={(event) =>
                          setDrafts((current) => ({
                            ...current,
                            [item.id]: { ...draft, creditAmount: event.target.value },
                          }))
                        }
                      />
                    ) : (
                      <span />
                    )}
                    <input
                      className="rounded-xl border px-3 py-2"
                      placeholder={draft.itemStatus === 'denied' ? 'What happened to the part' : 'Note'}
                      required={draft.itemStatus === 'denied'}
                      value={draft.resolutionNote}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [item.id]: { ...draft, resolutionNote: event.target.value },
                        }))
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
          <label className="block text-sm font-medium">
            Credit memo number
            <input className="mt-1 w-full rounded-xl border px-3 py-2" value={memo} onChange={(e) => setMemo(e.target.value)} />
          </label>
          <button
            type="submit"
            disabled={resolveMutation.isPending}
            className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {resolveMutation.isPending ? 'Saving…' : 'Resolve RMA'}
          </button>
        </form>
      ) : null}
    </div>
  );
}
